import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {UserProfileService} from './user-profile.service';
import {SiteId, SiteInfo} from './access-sites.interface';
import {
  BuildingType,
  Currency,
  MeterStream,
  Site,
  WorkingHours,
  SiteToUserIdByUserEntityIdMapping,
  Maybe,
  Level,
  Zone,
  Scalars,
  DateTime,
  DemandRate,
  NabersBoundsSchedule,
  Meter,
  OccupiedMeterage,
  SiteExternalReference,
  ThermalComfortSchedule
} from '@app/graphql-typings/graphql';
import {BaseService} from './base-service';
import {PagedModel} from '../../api/model/paged-model';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import * as gql from 'gql-query-builder';
import {ClassTransformer, Transform} from 'class-transformer';
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {PageInfo} from '@shared/components/ace-table/ace-table.component';
import IQueryBuilderOptions from 'gql-query-builder/build/IQueryBuilderOptions';
import Fields from 'gql-query-builder/build/Fields';
import {BaseIntervalRefreshedCache} from '@shared/caches/interval-refreshed-cache';
import {WebBrowserCommunicationService} from '@shared/services/web-browser/web-browser-communication.service';
import {LicenseTier} from '@app/shared/util/general/license';
import {AngularPageVisibilityService} from '../web-browser/page-visibility.service';
import {MeterId} from './meter-provider.interface';
import {MapUtils} from '@app/shared/util/collections/collection-utils';

export interface SitesSearchTerm {
  siteIds?: number[];
  site_name?: string;
  clientId?: number;
}


class SiteImpl implements Site {
  levels: Maybe<Array<Maybe<Level>>>;
  zones: Maybe<Array<Maybe<Zone>>>;
  address_line_1: string;
  address_line_2: string | null;
  base_temperature: number | null;
  building_type: Maybe<Scalars['BuildingTypeEnum']>;
  city: string;
  client_id: number | null;
  client_mappings: SiteToUserIdByUserEntityIdMapping | null;
  clients: number[] | null;
  consulting_start_ts: DateTime | null;
  contractor_target_closure_rate: number;
  contractor_mappings: SiteToUserIdByUserEntityIdMapping | null;
  customer_mappings: SiteToUserIdByUserEntityIdMapping | null;
  cop: number;
  country: string;
  customer_id: number;
  demand_peak_billing_model: string | null;
  demand_peak_schedule: JSON | null;
  demand_rates: DemandRate[] | null;
  electricity_charge_rate: number | null;
  email_address: string | null;
  first_report_issued_ts: DateTime | null;
  first_resolved_issue_ts: DateTime | null;
  gas_charge_rate: number | null;
  geo_coordinates: string | null;
  ideal_relative_humidity_margin: number;
  ideal_relative_humidity_max: number;
  ideal_relative_humidity_min: number;
  is_active: boolean;
  monetary_currency: Currency;
  nabers_bounds_schedules: Maybe<Array<NabersBoundsSchedule>>;
  external_references: Maybe<Array<SiteExternalReference>>;
  nmi_numbers: Meter[] | null;
  nmi_streams: MeterStream[] | null;
  norm_intercept: number | null;
  norm_occupancy_co: number | null;
  norm_slope: number | null;
  occupied_meterages: OccupiedMeterage[] | null;
  postcode: string;
  potential_annual_savings: number | null;
  potential_annual_savings_gas: number | null;
  potential_annual_savings_water: number | null;
  provider_id: number | null;
  reporting_start_ts: DateTime | null;
  site_id: number;
  site_name: string;
  state: string;
  thermal_comfort_humidity_available: boolean | null;
  thermal_comfort_margin: number;
  thermal_comfort_max_temp: number;
  thermal_comfort_min_temp: number;
  thermalcomfort_schedule: Maybe<ThermalComfortSchedule>;
  timezone: string;
  water_charge_rate: number | null;
  working_hours: WorkingHours | null;
  license_tier: LicenseTier;
  photo_url?: string | null;
  building_size?: number | null;
  equipment_health_target_pass_rate?: number;
}

class SiteInfoImpl implements SiteInfo {
  address_line_1: string;
  address_line_2: string;
  building_type: BuildingType;
  city: string;

  client_mappings: SiteToUserIdByUserEntityIdMapping | null;
  customer_mappings: SiteToUserIdByUserEntityIdMapping | null;
  contractor_mappings: SiteToUserIdByUserEntityIdMapping | null;

  clients: number[];
  country: string;
  first_report_issued_ts: string;
  geo_coordinates: string;

  @Transform(({obj}: {obj: SiteInfo}) => obj.first_report_issued_ts)
  involvement_date: string;

  monetary_currency: Currency | null;
  nmi_numbers: Meter[];
  postcode: string;
  site_id: number;
  site_name: string;
  state: string;
  thermal_comfort_humidity_available: boolean;
  thermal_comfort_weather_available: boolean;
  timezone: string;
  working_hours: WorkingHours;
  license_tier: LicenseTier;
  photo_url?: string | null;
  equipment_health_target_pass_rate?: number | null;
}

const workingHoursGraphqlFields = ['mondayEnabled',
                                   'mondayStart',
                                   'mondayEnd',
                                   'tuesdayEnabled',
                                   'tuesdayStart',
                                   'tuesdayEnd',
                                   'wednesdayEnabled',
                                   'wednesdayStart',
                                   'wednesdayEnd',
                                   'thursdayEnabled',
                                   'thursdayStart',
                                   'thursdayEnd',
                                   'fridayEnabled',
                                   'fridayStart',
                                   'fridayEnd',
                                   'saturdayEnabled',
                                   'saturdayStart',
                                   'saturdayEnd',
                                   'sundayEnabled',
                                   'sundayStart',
                                   'sundayEnd'];

const fullSitesGQLQuery = gql.query({
  operation: 'sites',
  fields: [
    'site_id',
    'site_name',
    'address_line_1',
    'address_line_2',
    'city',
    'state',
    'postcode',
    'country',
    'geo_coordinates',
    'timezone',
    'email_address',
    'provider_id',
    'electricity_charge_rate',
    'gas_charge_rate',
    'water_charge_rate',
    'potential_annual_savings',
    'potential_annual_savings_gas',
    'potential_annual_savings_water',
    'cop',
    'customer_id',
    'first_report_issued_ts',
    'monetary_currency',
    'building_type',
    'thermal_comfort_humidity_available',
    'thermal_comfort_min_temp',
    'thermal_comfort_max_temp',
    'thermal_comfort_margin',
    'ideal_relative_humidity_min',
    'ideal_relative_humidity_max',
    'ideal_relative_humidity_margin',
    'contractor_target_closure_rate',
    'is_active',
    'client_mappings',
    'contractor_mappings',
    'customer_mappings',
    'demand_peak_billing_model',
    'license_tier',
    'photo_url',
    'building_size',
    'equipment_health_target_pass_rate',
    {
      working_hours: workingHoursGraphqlFields
    },
    {
      occupied_meterages: ['occupied_start_ts', 'square_meterage']
    },
    {
      demand_rates: ['start_ts', 'rate']
    },
    {
      demand_peak_schedule: ['day_start', 'day_end', 'exclude_weekends']
    },
    {
      thermalcomfort_schedule: ['day_start', 'day_end', 'exclude_weekends']
    },
    {
      nmi_numbers: [
        'nmi_id',
        'nmi_number',
        'provider_id',
        'timezone',
        'data_interval',
        'demand_peak_calculation_interval',
        'utility_type',
        'meter_type',
        {
          nmi_streams: ['stream_id']
        }
      ]
    },
    {
      nabers_bounds_schedules: [
        'nabers_bounds_schedule_id',
        'site_id',
        'start_date',
        'bound_0',
        'bound_1',
        'bound_2',
        'bound_3',
        'bound_4',
        'bound_5',
        'bound_6',
        'bound_7',
        'bound_8',
        'bound_9',
        'bound_10',
        'bound_11',
        'bound_12',
      ]
    },
    {
      external_references: ['site_reference_id', 'label', 'external_reference']
    }
  ],
  variables: {
    site_ids: {
      type: GRAPHQL_TYPES.INTEGER,
      list: true
    },
    site_name: {
      type: GRAPHQL_TYPES.STRING
    },
    client_id: {
      type: GRAPHQL_TYPES.INTEGER
    },
    start_index: {
      type: GRAPHQL_TYPES.INTEGER
    },
    limit: {
      type: GRAPHQL_TYPES.INTEGER
    }
  }
});


const nmiStreamQuery: IQueryBuilderOptions = {
  operation: 'nmi_streams',
  fields: [
    'nmi_id',
    'stream_id',
    'description',
    'equipment_id',
    'reportable',
    'norm_slope',
    'norm_occupancy_co',
    'norm_intercept',
    'base_temperature',
    {
      nmi: ['nmi_id', 'nmi_number', 'utility_type', 'data_interval']
    }
  ],
  variables: {
    reportable: { type: GRAPHQL_TYPES.BOOLEAN, value: true }
  }
};


const siteInfoFields: Fields = [
  'site_id',
  'site_name',
  'address_line_1',
  'address_line_2',
  'city',
  'state',
  'postcode',
  'country',
  'monetary_currency',
  'building_type',
  'geo_coordinates',
  'timezone',
  'first_report_issued_ts',
  'thermal_comfort_weather_available',
  'thermal_comfort_humidity_available',
  'clients',
  'client_mappings',
  'customer_mappings',
  'contractor_mappings',
  'electricity_charge_rate',
  'water_charge_rate',
  'gas_charge_rate',
  'license_tier',
  'photo_url',
  'building_size',
  'equipment_health_target_pass_rate',
  {
    working_hours: workingHoursGraphqlFields
  },
  nmiStreamQuery
];


const siteInfoGQLQuery = gql.query({
  operation: 'sites',
  fields: siteInfoFields,
  variables: {
    start_index: {
      type: GRAPHQL_TYPES.INTEGER
    },
    limit: {
      type: GRAPHQL_TYPES.INTEGER
    }
  }
});


interface SiteInfoWithReportableStreams extends SiteInfo {
  nmi_streams: MeterStream[];
}


@Injectable()
export class SiteService extends BaseService {
  private static readonly modelMapping = { Site: SiteImpl, SiteInfo: SiteInfoImpl };
  private readonly FETCH_PAGE_SIZE = 500;
  private readonly logger: AceLogger;
  private readonly siteCache: SiteCache;

  constructor(private userProfileService: UserProfileService,
              private pageVisibilityService: AngularPageVisibilityService,
              private classTransformer: ClassTransformer,
              private httpUtilityService: HttpUtilityService,
              browserCommsService: WebBrowserCommunicationService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(SiteService.name);
    this.siteCache = new SiteCache(this.pageVisibilityService, browserCommsService, this.userProfileService, logService);
    this.siteCache.init(this.fetchSitesWithReportableNMIs, 'site_id');
  }

  /**
   * Not for public consumption!
   * Use getCachedSiteInfos() instead
   */
  private readonly fetchSitesWithReportableNMIs: () => Observable<SiteInfo[]> = () => {
    const fetchFn = (pageInfo: PageInfo) => this.httpUtilityService.graphQL(siteInfoGQLQuery, 'sites', { start_index: pageInfo.first, limit: pageInfo.rows });
    return this.httpUtilityService.pageUntilComplete(fetchFn, { first: 0, rows: this.FETCH_PAGE_SIZE }).pipe(map((sites: SiteInfoWithReportableStreams[]) => {
      sites.forEach(site => {
        const metersById = new Map<MeterId, Meter>();
        site.nmi_streams?.forEach(meterStream => {
          MapUtils.setDefaultFn(metersById, meterStream.nmi_id, () => ({...meterStream.nmi, nmi_streams: []})).nmi_streams.push(meterStream);
        });
        site.nmi_numbers = Array.from(metersById.values());
        delete site.nmi_streams;
      });
      return this.classTransformer.plainToInstance(SiteService.modelMapping['SiteInfo'], sites);
    }));
  };

  /**
   * Same as {@link #getCachedSiteInfos} but for only for one site instead of multiple. siteId has to be provided.
   * @param siteId the id of the site to retrieve information
   * @param ignoreCache whether to bypass the cache
   */
  getCachedSiteInfo(siteId: number, ignoreCache = false): Observable<SiteInfo> {
    if (siteId) {
      return this.getCachedSiteInfos([siteId], ignoreCache).pipe(map((siteInfos: SiteInfo[]) => {
        if (siteInfos && siteInfos.length) {
          return siteInfos[0];
        }

        return null;
      }));
    } else {
      throw new Error('Site id cannot be null');
    }
  }

  /**
   * Retrieve a Map of the most commonly used site properties keyed by site id. Optionally pass site ids of the sites to
   * return. If site ids are not passed, information about all sites the user has permission to see is returned.
   *
   * Unless ignoreCache is set to true, the data will be retrieved from the cache.
   *
   * @param siteIds if passed, return the information only about the provided sites, otherwise all sites the user has permission to see are
   * returned.
   * @param ignoreCache whether to refresh the cache before returning the data, effectively bypassing the cache
   * @see {@link getSites} for retrieving full site data
   */
  getCachedSiteInfosById(siteIds?: number[], ignoreCache = false): Observable<Map<number, SiteInfo>> {
    return this.siteCache.getItemsById(siteIds, ignoreCache);
  }

  /**
   * Retrieve the most commonly used site properties. Optionally pass site ids of the sites to return. If site ids
   * are not passed, information about all sites the user has permission to see is returned.
   *
   * Unless ignoreCache is set to true, the data will be retrieved from the cache.
   *
   * @param siteIds if passed, return the information only about the provided sites, otherwise all sites the user has permission to see are
   * returned.
   * @param ignoreCache whether to refresh the cache before returning the data, effectively bypassing the cache
   * @see {@link getSites} for retrieving full site data
   */
  getCachedSiteInfos(siteIds?: number[], ignoreCache = false): Observable<SiteInfo[]> {
    return this.getCachedSiteInfosById(siteIds, ignoreCache).pipe(map(siteInfosById => Array.from(siteInfosById.values())));
  }

  /**
   * Same as {@link getSites} but for one site. The siteId must be supplied.
   * @param siteId the id of the site for which to retrieve information
   */
  getSite(siteId: number): Observable<Site> {
    if (siteId) {
      return this.getSites({siteIds: [siteId]}).pipe(map(pageModel => {
        if (pageModel && pageModel.data && pageModel.data.length) {
          return pageModel.data[0];
        }

        return null;
      }));
    } else {
      throw new Error('Site id is null');
    }
  }

  /**
   * Retrieves full site information. If siteIds are not passed (or null) then information about all sites the user has permission to see
   * is returned. Otherwise, only information about sites for given ids is returned.
   * @param fields
   * - siteIds: ids of sites for which the information should be retrieved. The null value (or not passing one) indicates all sites
   * the user has permission to see<br>
   * - site_name: search site name
   * @param pageInfo page information i.e. offset and number of records to fetch
   * @see {@link getCachedSiteInfos} for retrieving commonly used site information from a cache (which is much faster)
   */
  getSites(fields?: SitesSearchTerm, pageInfo?: {first: number, rows: number}): Observable<PagedModel<Site[]>> {

    const siteQueryParams = {};

    if (fields) {
      if (fields.siteIds) {
        if (fields.siteIds.length) {
          siteQueryParams['site_ids'] = fields.siteIds;
        } else {
          return of({ count: 0, data: [] });
        }
      }

      if (fields.site_name && fields.site_name.trim()) {
        siteQueryParams['site_name'] = fields.site_name.trim();
      }

      if (fields.clientId) {
        siteQueryParams['client_id'] = fields.clientId;
      }
    }

    const fetchSitesByPageInfo = ({ first, rows }: PageInfo) => this.httpUtilityService.graphQL<Site[]>(fullSitesGQLQuery, 'sites', {
      ...siteQueryParams,
      start_index: first,
      limit: rows
    });

    const observable = pageInfo
        ? fetchSitesByPageInfo(pageInfo)
        : this.httpUtilityService.pageUntilComplete(fetchSitesByPageInfo, { first: 0, rows: this.FETCH_PAGE_SIZE })
            .pipe(map((sites: Site[]) => (<PagedModel<Site[]>>{ data: sites, count: sites.length })));

    return observable.pipe(
        map((pagedSites: PagedModel<Site[]>) => ({
          data: this.classTransformer.plainToInstance(SiteService.modelMapping['Site'], pagedSites.data),
          count: pagedSites.count
        })));
  }

  updateCachedSiteInfo(id: SiteId, updateFn: (siteInfo: SiteInfo) => SiteInfo): void {
    this.siteCache.updateItemIfExists(id, updateFn);
  }
}


class SiteCache extends BaseIntervalRefreshedCache<SiteInfo, 'site_id'> {

  // Need to keep a static array of SiteCache(s) as the Page Visibility events are not fired with reference to SiteCache
  // instance (i.e. this is undefined inside the Page Visibility event listeners). Therefore, need a way to get to the
  // SiteCache instance
  static LISTENERS_UGLY_HACK: SiteCache[] = [];

  constructor(pageVisibilityService: AngularPageVisibilityService,
              browserCommsService: WebBrowserCommunicationService,
              userProfileService: UserProfileService,
              logService: AceLoggerService) {
    super(pageVisibilityService, browserCommsService, userProfileService, logService.create(SiteCache.name), WebBrowserCommunicationService.KEYS.SITES);
  }

  populateStaticCacheArrayHack() {
    SiteCache.LISTENERS_UGLY_HACK.push(this);
  }

  whenPageVisible(): void {
    SiteCache.onPageVisible(SiteCache.LISTENERS_UGLY_HACK);
  }
}
